import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowDown, Globe, Play, Sparkles, Zap, Check } from "lucide-react";
import { scrollToId } from "@/lib/site";
import { GlowButton } from "@/components/primitives";

const ease = [0.22, 1, 0.36, 1];

export default function Hero() {
  const reduce = useReducedMotion();

  return (
    <section id="home" className="relative overflow-hidden pt-28 pb-16 sm:pt-32 lg:pt-40 lg:pb-24" data-testid="hero-section">
      {/* decorative background shapes */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -right-24 top-10 h-[420px] w-[420px] rounded-full bg-[#FF6B35]/10 blur-3xl animate-blob" />
        <div className="absolute -left-32 top-1/3 h-[380px] w-[380px] rounded-full bg-[#7C3AED]/10 blur-3xl animate-blob [animation-delay:-7s]" />
        <div className="absolute inset-x-0 top-0 h-full dotted-grid opacity-[0.5] [mask-image:radial-gradient(ellipse_at_top_right,black,transparent_70%)]" />
      </div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10 lg:px-8">
        {/* Left copy */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 shadow-sm ring-1 ring-slate-200"
          >
            <Sparkles className="h-3.5 w-3.5 text-[#FF6B35]" />
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-600">
              Modern creative technology studio
            </span>
          </motion.div>

          <h1 className="font-display text-[2.6rem] font-extrabold leading-[1.02] tracking-tight text-[#111827] sm:text-6xl lg:text-7xl">
            <span className="block overflow-hidden">
              <motion.span
                className="inline-block"
                initial={reduce ? { opacity: 0 } : { y: "110%" }}
                animate={reduce ? { opacity: 1 } : { y: 0 }}
                transition={{ duration: 0.85, delay: 0.1, ease }}
              >
                Digital experiences.
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span
                className="inline-block"
                initial={reduce ? { opacity: 0 } : { y: "110%" }}
                animate={reduce ? { opacity: 1 } : { y: 0 }}
                transition={{ duration: 0.85, delay: 0.24, ease }}
              >
                Built <span className="text-gradient">smarter.</span>
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-6 max-w-xl text-lg text-slate-600"
          >
            Websites, AI-powered video ads and intelligent automations for businesses ready to move
            faster.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.62 }}
            className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <GlowButton onClick={() => scrollToId("contact")} className="w-full sm:w-auto" data-testid="hero-cta-start-project">
              Start a Project
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </GlowButton>
            <GlowButton variant="secondary" onClick={() => scrollToId("services")} className="w-full sm:w-auto" data-testid="hero-cta-explore-services">
              Explore Services
              <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
            </GlowButton>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-medium text-slate-500"
          >
            <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#2563EB]" /> Websites</span>
            <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#FF6B35]" /> AI Video Ads</span>
            <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#65A30D]" /> Automations</span>
          </motion.div>
        </div>

        {/* Right composition */}
        <HeroComposition reduce={reduce} />
      </div>
    </section>
  );
}

function HeroComposition({ reduce }) {
  const pop = (delay) => ({
    initial: { opacity: 0, y: 28, scale: 0.95 },
    animate: { opacity: 1, y: 0, scale: 1 },
    transition: { duration: 0.8, delay, ease },
  });

  return (
    <div className="relative mx-auto h-[400px] w-full max-w-md sm:h-[460px] lg:h-[520px] lg:max-w-none">
      {/* color shapes */}
      <div className="absolute right-6 top-6 h-44 w-44 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FFB08A] opacity-90 blur-[2px]" />
      <div className="absolute bottom-10 left-2 h-28 w-28 rounded-[2rem] bg-gradient-to-br from-[#2563EB] to-[#7C3AED] rotate-12" />
      <div className="absolute bottom-24 right-10 h-16 w-16 rounded-full bg-[#B7F34A] ring-8 ring-[#B7F34A]/20" />

      {/* Browser card (Website) */}
      <motion.div
        {...pop(0.35)}
        className={`absolute left-0 top-4 z-20 w-[72%] rounded-2xl bg-white p-3 shadow-float ring-1 ring-slate-100 ${reduce ? "" : "animate-float"}`}
      >
        <div className="mb-2 flex items-center gap-1.5 border-b border-slate-100 pb-2">
          <span className="h-2 w-2 rounded-full bg-[#FF6B35]" />
          <span className="h-2 w-2 rounded-full bg-[#FFC94A]" />
          <span className="h-2 w-2 rounded-full bg-[#65A30D]" />
          <span className="ml-2 flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5">
            <Globe className="h-2.5 w-2.5 text-slate-400" />
            <span className="text-[9px] text-slate-400">digitaladvyzer.com</span>
          </span>
        </div>
        <div className="h-3 w-20 rounded-full bg-[#2563EB]" />
        <div className="mt-2 h-4 w-3/4 rounded-md bg-slate-200" />
        <div className="mt-1.5 h-4 w-1/2 rounded-md bg-slate-100" />
        <div className="mt-3 flex gap-2">
          <div className="h-6 w-20 rounded-full bg-gradient-to-r from-[#2563EB] to-[#7C3AED]" />
          <div className="h-6 w-14 rounded-full ring-1 ring-slate-200" />
        </div>
      </motion.div>

      {/* Video frame (AI Video Ads) */}
      <motion.div
        {...pop(0.55)}
        className={`absolute right-0 top-24 z-30 w-[44%] overflow-hidden rounded-2xl bg-white p-2 shadow-float ring-1 ring-slate-100 ${reduce ? "" : "animate-float [animation-delay:-3s]"}`}
      >
        <div className="relative aspect-[9/14] overflow-hidden rounded-xl bg-gradient-to-br from-[#7C3AED] via-[#9333EA] to-[#FF6B35]">
          <div className="absolute left-2 top-2 rounded-full bg-white/25 px-2 py-0.5 text-[8px] font-semibold uppercase tracking-wide text-white backdrop-blur-sm">
            AI Reel
          </div>
          <div className="absolute left-1/2 top-1/2 grid h-10 w-10 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white shadow-lg">
            <Play className="h-4 w-4 translate-x-[1px] fill-[#7C3AED] text-[#7C3AED]" />
          </div>
          <div className="absolute inset-x-2 bottom-5 flex items-end gap-0.5">
            {[6, 10, 4, 12, 7, 11, 5, 9].map((h, i) => (
              <motion.span
                key={i}
                className="w-full rounded-full bg-white/80"
                animate={reduce ? {} : { height: [`${h}px`, `${h + 6}px`, `${h}px`] }}
                transition={{ duration: 1.1, repeat: Infinity, delay: i * 0.08 }}
                style={{ height: `${h}px` }}
              />
            ))}
          </div>
          <div className="absolute inset-x-2 bottom-2 h-1 overflow-hidden rounded-full bg-white/30">
            <motion.div className="h-full bg-white" animate={reduce ? {} : { width: ["10%", "100%"] }} transition={{ duration: 3.5, repeat: Infinity, ease: "linear" }} />
          </div>
        </div>
      </motion.div>

      {/* Workflow card (Automation) */}
      <motion.div
        {...pop(0.75)}
        className={`absolute bottom-0 left-6 z-20 w-[64%] rounded-2xl bg-white p-4 shadow-float ring-1 ring-slate-100 ${reduce ? "" : "animate-float [animation-delay:-5s]"}`}
      >
        <div className="mb-2.5 flex items-center gap-2">
          <span className="grid h-6 w-6 place-items-center rounded-lg bg-[#F3FAE6] text-[#65A30D]">
            <Zap className="h-3.5 w-3.5" />
          </span>
          <span className="text-xs font-semibold text-[#111827]">Automation</span>
        </div>
        <div className="flex items-center gap-1.5">
          {["Lead", "AI", "Action", "Done"].map((label, i) => (
            <div key={label} className="flex items-center gap-1.5">
              <div className="flex flex-col items-center gap-1">
                <span
                  className="grid h-7 w-7 place-items-center rounded-lg text-[9px] font-bold text-white"
                  style={{ background: ["#2563EB", "#7C3AED", "#FF6B35", "#65A30D"][i] }}
                >
                  {i === 3 ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </span>
                <span className="text-[8px] font-medium text-slate-500">{label}</span>
              </div>
              {i < 3 && <span className="h-px w-3 bg-slate-300" />}
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
